/**
 * 학습 계획 파생 상태 계산(순수, 서버·클라 공용). (JCG-2310)
 *
 * 입력: 정적 StudyPlan(study-plan-index.json) + 사용자 동적 상태(구독 시작일·오버라이드·출석 slug) + 오늘 날짜.
 * 출력: 날짜로 앵커링된 task 인스턴스 + 진행/온트랙 지표.
 *
 * "오늘의 학습" 위젯 · 온트랙 카드 · /my/schedule 캘린더가 모두 이 모듈로 동일하게 파생한다.
 * Date.now() 를 쓰지 않고 todayISO 를 인자로 받아 테스트 가능하게 유지한다.
 */
import {
  type StudyPlan,
  type StudyPlanId,
  type StudyPlanTask,
  type StudyTaskKind,
  STUDY_PLAN_IDS,
  addDaysISO,
  diffDaysISO,
} from "./study-plan";
import { shouldShowAttendanceTool } from "./route-layout";
import { contentSlugOf, isSharedAttendanceSlug } from "./code-lang";
import { toKstDateStr } from "./kst-date";

export type TaskStatus = "none" | "done" | "skipped";

export interface TaskOverride {
  status: TaskStatus;
  movedToDate: string | null;
  /** 같은 날(칼럼) 안에서의 정렬값(분수 인덱싱). 없으면(undefined/null) 기본 순서 사용. */
  sortOrder?: number | null;
  /**
   * 완료 시각(ISO) — "언제 끝냈는지"라는 학습 사실. 2회독 이후(자체 완료)에만 적힌다. (JCG-3547)
   * 1회독(출석 연동)의 원천은 attendance_records.created_at 이다.
   * JCG-3653 부터 배치에는 쓰지 않는다 — 자리는 completionDate 가 정한다. 설정을 켜면서
   * "기존 완료 항목도 옮기기"를 고를 때 completionDate 를 만드는 원천으로만 쓴다.
   */
  completedAt?: string | null;
  /**
   * 완료 항목이 캘린더에 그려질 KST 날짜(YYYY-MM-DD). null 이면 예정 날짜에 남는다. (JCG-3653)
   * 적히는 시점: 자동 이동 설정 on 상태에서 완료할 때(그날), 완료 항목을 다른 날로 끌었을 때
   * (그날), 설정을 켜며 기존 완료 항목을 일괄로 옮길 때(완료 시각의 날짜). 완료를 취소하면 지운다.
   * 자리를 계산하지 않고 **저장**하므로 설정을 켜고 끄는 것 자체는 과거 완료 항목을 움직이지 않는다.
   */
  completionDate?: string | null;
}

/** 자동 이동 토글 팝업("기존 완료 항목도 옮길까요?")의 "다시 묻지 않기" 기억값. (JCG-3653) */
export type ExistingItemsPolicy = "ask" | "apply" | "keep";
export const EXISTING_ITEMS_POLICIES: readonly ExistingItemsPolicy[] = [
  "ask",
  "apply",
  "keep",
];

/** 서버액션이 내려주는 사용자별 동적 상태(계획 1개분) */
export interface PlanDynamicState {
  subscribed: boolean;
  /** 개인 시작일(미구독이면 계획 정규 시작일) */
  startDateISO: string;
  /** task_key -> override */
  overrides: Record<string, TaskOverride>;
  /**
   * 조회 회차 출석 매칭 키 → 출석 시각(ISO). (JCG-2658, JCG-3547)
   * 각 출석 레코드마다 순수 slug 와 (언어별이면) slug::codeLang 을 함께 담는다 →
   * 언어별 task 는 slug::lang, 언어 무관 task 는 slug 로 매칭(attendanceKeyOf 참조).
   * 값은 attendance_records.created_at — 1회독 완료 항목은 이 시각의 날짜에 배치된다.
   * 순수 slug 키에는 그 페이지 출석 중 가장 이른 시각을 담는다(언어 무관 task 의 "처음 공부한 날").
   */
  attendedAt: Record<string, string>;
  /** 구독 시점 계획과 기본 배치. 공통 계획 수정으로 기존 일정을 재분배하지 않는다. */
  scheduleSnapshot?: StudyScheduleSnapshot;
  /**
   * 완료 항목을 완료한 날짜로 자동 이동할지(사용자 설정). 기본 off. (JCG-3653)
   *
   * JCG-3547 은 이 이동을 끌 수 없게 도입했고, 배포 즉시 기존 완료 항목 전부가 실제 학습일로
   * 옮겨져 사용자 전원이 손으로 되돌렸다(JCG-3650). 그래서 설정으로 빼고 기본값을 off 로 둔다 —
   * **미지정(undefined)은 off** 다.
   *
   * 이 값은 **완료하는 순간**에만 읽힌다(그날을 completionDate 에 적을지). 배치 계산에는 쓰지
   * 않으므로 켜고 끄는 것만으로는 이미 완료한 항목이 움직이지 않는다 — 과거 항목은 토글 팝업에서
   * 일괄 적용/되돌리기를 고를 때만 움직인다. 사용자 단위 설정(study_plan_preferences)이라
   * 한 사용자의 모든 계획에 같은 값이 실린다.
   */
  completionMoveEnabled?: boolean;
  /** 토글 팝업의 "다시 묻지 않기" 기억값. 미지정은 "ask". (JCG-3653) */
  completionMoveExistingPolicy?: ExistingItemsPolicy;
  /**
   * 학습 완료 후 다음 일정 안내 시트를 띄울지. 미지정은 true. 사용자 단위 설정이라 모든 계획에
   * 같은 값이 실린다. (JCG-3677)
   */
  nextStudyGuideEnabled?: boolean;
}

export interface StudyScheduleSnapshot {
  schemaVersion: 1;
  plan: StudyPlan;
  positions: Record<
    string,
    {
      dateISO: string;
      sortOrder: number | null;
      attendanceBacked: boolean;
    }
  >;
}

export function personalStudyPlan(
  plan: StudyPlan,
  dyn: PlanDynamicState,
): StudyPlan {
  return dyn.scheduleSnapshot?.plan ?? plan;
}

/** 계획별 동적 상태 맵(클라 낙관적 갱신 단위). null = 아직 미로드. */
export type PlanDynamicStateMap = Record<StudyPlanId, PlanDynamicState> | null;

export interface ScheduledTask {
  planId: StudyPlanId;
  /** 렌더·DnD 전용 고유 id(같은 날 같은 path 도 구분). React key·dnd id 용 */
  uid: string;
  /** '{originDayNumber}:{anchor 제거 경로}' — 오버라이드 키(같은 path 면 완료 공유) */
  taskKey: string;
  originDayNumber: number;
  indexInDay: number;
  title: string;
  path: string;
  /** 출석 매칭 slug(leading slash·anchor 제거) */
  slug: string;
  /** 다국어 코딩 페이지의 언어('c'|'java'|'python'), 언어 무관이면 ''. (JCG-2658) */
  codeLang: string;
  /** 페이지·언어별 회독. 단계에 명시된 회독은 상한으로 사용한다. */
  reviewRound?: number;
  /** 기존 일정 키를 보존하기 위한 저장 언어. 표시·출석 언어와 다를 수 있다. */
  storageCodeLang?: string;
  examSessionId?: string;
  emoji: string;
  kind: StudyTaskKind;
  probability: number | null;
  isRelated: boolean;
  /** 출석 버튼이 노출되는(=출석으로 완료 가능한) 페이지인지 */
  attendanceEligible: boolean;
  /** 이 taskKey의 완료 상태를 실제 출석 레코드와 연동하는지 */
  attendanceBacked: boolean;
  /** 완료 방식과 별개로 이 항목에 표시할 출석 기록이 있는지 */
  attendanceRecorded: boolean;
  done: boolean;
  skipped: boolean;
  movedToDate: string | null;
  /** 이동이 없을 때의 날짜(사본의 배치 날짜, 사본이 없으면 개인 시작일 기준 originDay 날짜). */
  plannedDateISO: string;
  /** 일정상 예정 날짜. movedToDate ?? plannedDateISO. 온트랙 지표·후보 날짜 대조의 기준. */
  scheduledDateISO: string;
  /**
   * 캘린더에 그려질 날짜. 완료했고 완료 날짜(override.completionDate)가 저장돼 있으면 그 날,
   * 아니면 예정 날짜다. (JCG-3653 — 완료 시각에서 파생하던 JCG-3547 모델을 저장 모델로 교체)
   */
  effectiveDateISO: string;
  /** 같은 날 안 정렬값(사용자 재정렬). null 이면 기본 순서. */
  sortOrder: number | null;
  /**
   * 완료 시각(ISO) — 학습 사실. 미완료면 null. override.completedAt ?? 출석 시각.
   * 배치에는 쓰지 않는다(JCG-3653). 설정을 켜며 기존 완료 항목을 일괄로 옮길 때의 날짜 원천이다.
   */
  completedAtISO: string | null;
  /**
   * 이 항목의 화면 날짜가 저장된 완료 날짜(override.completionDate)에서 나오는지. (JCG-3653)
   * = 완료 + completionDate 있음. 날짜 이동·되돌리기의 의미를 가른다 — true 면 드래그가 완료
   * 날짜를 다시 적고 "완료한 날짜 되돌리기"가 그 값을 지운다. false 면 예정 날짜(moved_to_date)를
   * 옮기는 일반 이동이다.
   */
  completionDrivesDate: boolean;
}

/** override 행이 "완전 기본값"인지 — 클라 낙관 갱신이 행을 지울지 판단할 때 서버 patchOverride 의 정리 규칙과 맞춘다. */
export function isDefaultOverride(o: TaskOverride): boolean {
  return (
    o.status === "none" &&
    o.movedToDate === null &&
    (o.sortOrder ?? null) === null &&
    (o.completedAt ?? null) === null &&
    (o.completionDate ?? null) === null
  );
}

/**
 * 자동 이동을 켜며 "기존 완료 항목도 옮기기"를 고르면 옮겨질 항목과 그 날짜. (JCG-3653)
 * 완료했지만 아직 완료 날짜가 없고, 완료 시각(completedAt ?? 출석 시각)을 아는 항목만이다 —
 * JCG-3547 이전에 자체 완료(2회독)한 항목은 시각이 없어 제외된다. 건너뛴 항목도 제외.
 */
export function completionMoveApplyTargets(
  tasks: ScheduledTask[],
): { task: ScheduledTask; completionDate: string }[] {
  const out: { task: ScheduledTask; completionDate: string }[] = [];
  for (const task of tasks) {
    if (!task.done || task.skipped || task.completionDrivesDate) continue;
    if (!task.completedAtISO) continue;
    out.push({ task, completionDate: toKstDateStr(task.completedAtISO) });
  }
  return out;
}

/** 자동 이동을 끄며 "옮겨둔 항목 되돌리기"를 고르면 예정 날짜로 돌아갈 항목. (JCG-3653) */
export function completionMoveRevertTargets(
  tasks: ScheduledTask[],
): ScheduledTask[] {
  return tasks.filter((task) => task.completionDrivesDate);
}

export type OnTrackStatus =
  | "not-started"
  | "ahead"
  | "on-track"
  | "slightly-behind"
  | "behind"
  | "done";

export interface ScheduleMetrics {
  planId: StudyPlanId;
  subscribed: boolean;
  startDateISO: string;
  /** 회차 고정 시험일(plan.examDateISO, 개인 시작일·계획 길이와 무관) */
  examDateISO: string;
  totalDays: number;
  todayDateISO: string;
  /** 개인 시작일 기준 오늘이 며칠차(1-based). 시작 전이면 null, 종료 후면 totalDays 초과값 */
  todayDayNumber: number | null;
  /** 건너뛰기 제외 전체 task 수 */
  trackableTasks: number;
  /** 완료 task 수(건너뛰기 제외) */
  doneTotal: number;
  /** 오늘까지 예정(effectiveDate <= today, 건너뛰기 제외) */
  scheduledByToday: number;
  /** 그 중 완료 */
  doneByToday: number;
  /** scheduledByToday - doneByToday (밀린 task 수) */
  backlog: number;
  /** 시험일까지 남은 일수(>=0) */
  daysUntilExam: number;
  /** 남은 task 수 */
  remainingTasks: number;
  /** 완주에 필요한 하루 페이스 */
  requiredPacePerDay: number;
  status: OnTrackStatus;
}

export interface DayGroup {
  dateISO: string;
  tasks: ScheduledTask[];
  doneCount: number;
  /** 건너뛰기 제외 task 수 */
  totalCount: number;
}

function stripPath(path: string): string {
  return path.split("#")[0];
}

/** 명시적인 언어 앵커는 저장 당시 잘못 추론한 언어보다 우선한다. 키는 변경하지 않는다. */
export function studyTaskCodeLang(
  task: Pick<StudyPlanTask, "path" | "codeLang">,
  examSessionId: string,
): string {
  if (!task.codeLang || !task.path.startsWith("/coding/common/"))
    return task.codeLang ?? "";
  // 언어 비교 페이지는 출석이 언어 무관 1건이다. 이미 저장된 개인 사본에 c/java/python 이
  // 태깅돼 있어도 매칭 단계에서 지워, 그 한 건의 출석으로 함께 완료되게 한다. (JCG-3655)
  // storageCodeLang(=task.codeLang)은 그대로 둬야 기존 taskKey·override 가 유지된다.
  // 도입 전 회차는 동결된 언어별 완료 상태를 그대로 유지한다.
  if (isSharedAttendanceSlug(task.path, examSessionId)) return "";
  const anchorLang = task.path
    .split("#")[1]
    ?.match(/(?:^|-)(python|java|c)$/i)?.[1]
    ?.toLowerCase();
  return anchorLang ?? task.codeLang;
}

/** 출석 매칭 slug: leading slash·anchor 제거 (attendance_records.slug 와 동일 형식) */
export const attendanceSlugOf = contentSlugOf;

/**
 * 출석 매칭 합성 키. (JCG-2658)
 * 다국어 코딩 페이지 task 는 언어별로 독립 완료되어야 하므로 slug 에 codeLang 을 붙인다.
 * codeLang 이 없으면(이론·단일언어·관련링크) 순수 slug 로 언어 무관 매칭.
 * attendedAt 맵에는 각 출석 레코드의 순수 slug 와 slug::lang 이 함께 들어가므로,
 * 언어별 task 는 slug::lang 로, 언어 무관 task 는 slug 로 각각 매칭된다.
 */
export function attendanceKeyOf(slug: string, codeLang?: string): string {
  return codeLang ? `${slug}::${codeLang}` : slug;
}

/**
 * 오버라이드 키: '{dayNumber}:{anchor 제거 경로}' (+ codeLang 있으면 '::{lang}'). (JCG-2658)
 * codeLang 없는 기존 task 는 이전과 동일한 키를 유지해 기존 override 와 호환된다.
 */
export function taskKeyOf(
  dayNumber: number,
  path: string,
  codeLang?: string,
): string {
  const base = `${dayNumber}:${stripPath(path)}`;
  return codeLang ? `${base}::${codeLang}` : base;
}

/**
 * 같은 출석 키(slug + 언어)가 계획에 여러 번 나오면 첫 taskKey 그룹만 출석과 연동한다.
 * 같은 날 같은 경로의 anchor 링크들은 taskKey를 공유하므로 함께 첫 그룹으로 취급한다.
 * 이후 회독 task는 날짜별 override로 독립 완료되어, 첫 출석이 후속 회독까지 미리 완료하거나
 * 후속 회독 체크가 기존 출석 레코드를 지우지 않게 한다. (JCG-3465)
 */
const attendanceBackedTaskKeysCache = new WeakMap<StudyPlan, Set<string>>();

function usesIndependentReviewTasks(examSessionId: string): boolean {
  const [year, round] = examSessionId.split("-").map(Number);
  return year > 2026 || (year === 2026 && round >= 3);
}

function attendanceBackedTaskKeys(plan: StudyPlan): Set<string> {
  const cached = attendanceBackedTaskKeysCache.get(plan);
  if (cached) return cached;

  const result = new Set<string>();
  const firstTaskKeyByAttendanceKey = new Map<string, string>();
  const independentReviews = usesIndependentReviewTasks(plan.examSessionId);
  for (const day of plan.days) {
    for (const task of day.tasks) {
      if (!task.path || !shouldShowAttendanceTool(task.path)) continue;
      const codeLang = task.codeLang ?? "";
      const slug = attendanceSlugOf(task.path);
      if (!slug) continue;
      const taskKey = taskKeyOf(day.dayNumber, task.path, codeLang);
      // JCG-3465 이전 회차는 이미 저장·노출된 완료 상태를 재해석하지 않는다.
      if (!independentReviews) {
        result.add(taskKey);
        continue;
      }
      const attendanceKey = attendanceKeyOf(slug, codeLang);
      if (!firstTaskKeyByAttendanceKey.has(attendanceKey)) {
        firstTaskKeyByAttendanceKey.set(attendanceKey, taskKey);
        result.add(taskKey);
      }
    }
  }

  attendanceBackedTaskKeysCache.set(plan, result);
  return result;
}

export function isAttendanceBackedTask(
  plan: StudyPlan,
  dayNumber: number,
  path: string,
  codeLang: string = "",
): boolean {
  // 선정 문제까지 마치는 계획이므로 기존 본문 출석과 완료 상태를 공유하지 않는다.
  if (plan.planId === "warrior-13day") return false;
  return attendanceBackedTaskKeys(plan).has(
    taskKeyOf(dayNumber, path, codeLang),
  );
}

/** 개인 계획의 원래 일차 순서로 회독을 구분한다. 이동·완료 상태로 순서를 바꾸지 않는다. */
const reviewRoundsCache = new WeakMap<StudyPlan, Map<string, number>>();

function reviewRoundsByTaskKey(plan: StudyPlan): Map<string, number> {
  const cached = reviewRoundsCache.get(plan);
  if (cached) return cached;

  const result = new Map<string, number>();
  const lastRound = new Map<string, number>();
  for (const day of [...plan.days].sort((a, b) => a.dayNumber - b.dayNumber)) {
    // 단계의 회독은 상한이다. 새 소주제는 1회독을 유지하고 혼합 단계의 첫 등장도 1회독이다.
    const phaseRound =
      Number(day.phaseLabel.match(/(\d+)\s*회독/)?.[1]) || null;
    for (const task of day.tasks) {
      if (!task.path || !shouldShowAttendanceTool(task.path)) continue;
      const key = taskKeyOf(day.dayNumber, task.path, task.codeLang);
      if (result.has(key)) continue;
      const pageKey = attendanceKeyOf(
        attendanceSlugOf(task.path),
        studyTaskCodeLang(task, plan.examSessionId),
      );
      const occurrence = (lastRound.get(pageKey) ?? 0) + 1;
      const round = phaseRound != null
        ? Math.min(phaseRound, occurrence)
        : occurrence;
      result.set(key, round);
      lastRound.set(pageKey, Math.max(lastRound.get(pageKey) ?? 0, round));
    }
  }
  reviewRoundsCache.set(plan, result);
  return result;
}

/**
 * 계획의 distinct 페이지(taskKey)를 커리큘럼 순서(Day → indexInDay 첫 등장)로 나열 + 위치 인덱스.
 * 작업량(페이지) 균등 분배의 위치 기준. 정적 계획당 1회 계산(WeakMap 캐시).
 */
const pageOrderCache = new WeakMap<
  StudyPlan,
  { order: string[]; index: Map<string, number> }
>();
export function planPageOrder(plan: StudyPlan): {
  order: string[];
  index: Map<string, number>;
} {
  const cached = pageOrderCache.get(plan);
  if (cached) return cached;
  const index = new Map<string, number>();
  const order: string[] = [];
  for (const day of plan.days) {
    for (const task of day.tasks) {
      const key = taskKeyOf(day.dayNumber, task.path, task.codeLang);
      if (!index.has(key)) {
        index.set(key, order.length);
        order.push(key);
      }
    }
  }
  const result = { order, index };
  pageOrderCache.set(plan, result);
  return result;
}

/** 다시 분배 분포 모델. even=매일 비슷 / front=초기열정(앞 多) / back=후기몰아치기(뒤 多) / pyramid=중기 집중. */
export type ReplanModel = "even" | "front" | "back" | "pyramid";

export const REPLAN_MODELS: ReplanModel[] = [
  "even",
  "front",
  "back",
  "pyramid",
];

/** 모델별 "그 날 작업량 비중" 가중치(D일). even=평탄 / front=앞 큼 / back=뒤 큼 / pyramid=중앙 큼. */
export function modelDayWeights(model: ReplanModel, D: number): number[] {
  const w = new Array<number>(D);
  for (let d = 0; d < D; d++) {
    switch (model) {
      case "front":
        w[d] = D - d; // 앞일수록 큰 비중
        break;
      case "back":
        w[d] = d + 1; // 뒤일수록 큰 비중
        break;
      case "pyramid":
        w[d] = Math.min(d + 1, D - d); // 중앙 최대
        break;
      default:
        w[d] = 1; // even
    }
  }
  return w;
}

/**
 * 페이지 0..P-1 을 anchor~exam(D=span+1 일)에 모델 가중치대로 분배한 "날짜 오프셋" 배열(길이 P). (JCG-2310)
 *
 * 각 날은 (그 날 가중치/총가중치)×P 만큼의 페이지를 받는다 — 가중치 누적(ceil)이 페이지 인덱스를
 * 넘는 시점의 날에 페이지를 채우는 좌측 정렬 방식. round(i·span/(P-1)) 식의 "양 끝 날이 반 토막"
 * (첫날=오늘이 옆날의 절반만 받던) 문제가 없다 — page0 은 항상 anchor(오늘)에 떨어지고, even 이면
 * 첫날도 ≈P/D 의 온전한 몫을 받는다(부족분은 마지막 날로 간다).
 */
export function pageDayOffsets(
  totalPages: number,
  span: number,
  model: ReplanModel = "even",
): number[] {
  if (totalPages <= 0) return [];
  const D = span + 1;
  if (totalPages === 1 || span <= 0)
    return new Array<number>(totalPages).fill(0);
  const w = modelDayWeights(model, D);
  const sum = w.reduce((a, b) => a + b, 0);
  const offsets = new Array<number>(totalPages);
  let prefix = 0;
  let cursor = 0;
  for (let d = 0; d < D; d++) {
    prefix += w[d];
    // ceil → 좌측 정렬(그 날 비중 몫을 그 날에 채움). page0 은 항상 day0(오늘)에 배정된다.
    const target = Math.min(totalPages, Math.ceil((prefix / sum) * totalPages));
    while (cursor < target) offsets[cursor++] = d;
  }
  while (cursor < totalPages) offsets[cursor++] = D - 1; // 잔여분은 마지막 날
  return offsets;
}

/**
 * 사본이 없을 때(= 사본을 처음 만들 때)의 기본 배치 규칙.
 * - `page`: 계획 페이지에 적힌 Day 날짜(day.dateISO) 그대로(JCG-3652). 구독·복귀가 시작일을 항상
 *   plan.startDateISO 로 쓰므로 개인 시작일 오프셋은 없다 — 미리보기·신규 구독·복귀가 모두 페이지 모양.
 * - `legacy-even`: 예전 규칙. 페이지 수 기준으로 개인 시작일~시험 전날에 균등 분배(pageDayOffsets).
 *   계획 페이지와 81~84% 어긋나 "원래 감자 일정으로 복귀"가 원래 모양을 만들지 못해 폐기했지만,
 *   사본 없이 남아 있는 옛 구독(JCG-3652 배포 시점 운영 1,883건)은 그때 보던 일정을 그대로 사본으로
 *   굳혀야 하므로 initialSnapshot 만 이 값을 쓴다. 그 밖의 균등 분배는 computeReplan 이 담당한다.
 */
export type BasePlacement = "page" | "legacy-even";

interface ScheduledTaskBuildContext {
  plan: StudyPlan;
  dyn: PlanDynamicState;
  reviewRounds: Map<string, number>;
  snapshotBackedAttendanceKeys: Set<string>;
  legacyOrder: ReturnType<typeof planPageOrder> | null;
  legacyOffsets: number[];
}

function scheduledTaskBuildContext(
  plan: StudyPlan,
  dyn: PlanDynamicState,
  basePlacement: BasePlacement = "page",
): ScheduledTaskBuildContext {
  plan = personalStudyPlan(plan, dyn);
  const reviewRounds = reviewRoundsByTaskKey(plan);
  const snapshotBackedAttendanceKeys = new Set<string>();
  if (dyn.scheduleSnapshot) {
    for (const day of plan.days) {
      for (const task of day.tasks) {
        const key = taskKeyOf(day.dayNumber, task.path, task.codeLang);
        if (!dyn.scheduleSnapshot.positions[key]?.attendanceBacked) continue;
        snapshotBackedAttendanceKeys.add(
          attendanceKeyOf(
            attendanceSlugOf(task.path),
            studyTaskCodeLang(task, plan.examSessionId),
          ),
        );
      }
    }
  }
  // 사본이 있으면 positions 가 자리를 정하므로 기본 배치는 계산하지 않는다.
  const legacyOrder =
    basePlacement === "legacy-even" && !dyn.scheduleSnapshot
      ? planPageOrder(plan)
      : null;
  // 시험날엔 일정을 넣지 않는다(시험날까지 공부할 순 없으므로) → span 에서 하루 뺀다.
  const legacyOffsets = legacyOrder
    ? pageDayOffsets(
        legacyOrder.order.length,
        Math.max(0, diffDaysISO(dyn.startDateISO, plan.examDateISO) - 1),
        "even",
      )
    : [];
  return {
    plan,
    dyn,
    reviewRounds,
    snapshotBackedAttendanceKeys,
    legacyOrder,
    legacyOffsets,
  };
}

function buildScheduledTask(
  context: ScheduledTaskBuildContext,
  day: StudyPlan["days"][number],
  task: StudyPlanTask,
  indexInDay: number,
): ScheduledTask {
  const {
    plan,
    dyn,
    reviewRounds,
    snapshotBackedAttendanceKeys,
    legacyOrder,
    legacyOffsets,
  } = context;
  const storageCodeLang = task.codeLang ?? "";
  const codeLang = studyTaskCodeLang(task, plan.examSessionId);
  const key = taskKeyOf(day.dayNumber, task.path, storageCodeLang);
  const position = dyn.scheduleSnapshot?.positions[key];
  if (dyn.scheduleSnapshot && !position) {
    throw new Error("저장된 학습 항목의 배치 정보가 없습니다.");
  }
  const override = dyn.overrides[key];
  const slug = attendanceSlugOf(task.path);
  const attendanceEligible = task.path
    ? shouldShowAttendanceTool(task.path)
    : false;
  const staticAttendanceBacked =
    attendanceEligible &&
    isAttendanceBackedTask(
      plan,
      day.dayNumber,
      task.path,
      storageCodeLang,
    );
  // 이전 직접 방문 처리에서 첫 일정의 연동을 해제한 사본을 읽을 때 복원한다.
  // 같은 출석 키가 다른 일정에 연결돼 있으면 그 기존 연결을 우선한다.
  const restoredFirstAttendance =
    position?.attendanceBacked === false &&
    staticAttendanceBacked &&
    !snapshotBackedAttendanceKeys.has(attendanceKeyOf(slug, codeLang));
  const attendanceBacked =
    restoredFirstAttendance || (position?.attendanceBacked ?? staticAttendanceBacked);
  // 언어별 task 는 slug::lang, 언어 무관 task 는 slug 로 출석 매칭(JCG-2658)
  const attendanceKey = attendanceKeyOf(slug, codeLang);
  const recordedAttendanceAtISO = dyn.attendedAt[attendanceKey] ?? null;
  const attendedAtISO =
    attendanceBacked && slug !== ""
      ? recordedAttendanceAtISO
      : null;
  // 출석 기록은 첫 연동 일정에만 표시한다. 이후 회독의 완료는 별도 override를 따른다.
  const attendanceRecorded = attendedAtISO !== null;
  // 분리된 첫 일정의 과거 수동 완료는 출석이 취소된 뒤 다시 완료로 보이면 안 된다.
  const done =
    (!restoredFirstAttendance && override?.status === "done") ||
    attendedAtISO !== null;
  const skipped = override?.status === "skipped";
  const movedToDate = override?.movedToDate ?? null;
  const plannedDateISO =
    position?.dateISO ??
    (legacyOrder
      ? addDaysISO(
          dyn.startDateISO,
          legacyOffsets[legacyOrder.index.get(key) ?? 0] ?? 0,
        )
      : day.dateISO);
  const scheduledDateISO = movedToDate ?? plannedDateISO;
  // 완료 시각은 학습 사실로만 채운다(2회독은 override.completedAt, 1회독은 출석 시각).
  const completedAtISO = done
    ? (restoredFirstAttendance ? attendedAtISO : (override?.completedAt ?? attendedAtISO))
    : null;
  // 완료 항목의 자리는 저장된 완료 날짜가 정한다(JCG-3653). 없으면 예정 날짜에 남는다 —
  // 계산하지 않고 저장하므로 설정 토글이 과거 완료 항목을 움직이지 않는다. 완료가 풀렸는데
  // 값이 남아 있는 경우(동시 편집 등)는 무시해 미완료 항목이 과거 칸에 서지 않게 한다.
  const completionDateISO =
    done && !restoredFirstAttendance ? (override?.completionDate ?? null) : null;
  const completionDrivesDate = completionDateISO !== null;
  const effectiveDateISO = completionDateISO ?? scheduledDateISO;

  return {
    planId: plan.planId,
    uid: `${plan.planId}:${day.dayNumber}:${indexInDay}`,
    taskKey: key,
    originDayNumber: day.dayNumber,
    indexInDay,
    title: task.title,
    path: task.path,
    slug,
    codeLang,
    storageCodeLang,
    examSessionId: plan.examSessionId,
    reviewRound: reviewRounds.get(key),
    emoji: task.emoji,
    kind: task.kind,
    probability: task.probability,
    isRelated: task.isRelated,
    attendanceEligible,
    attendanceBacked,
    attendanceRecorded,
    done,
    skipped,
    movedToDate,
    plannedDateISO,
    scheduledDateISO,
    effectiveDateISO,
    sortOrder: override?.sortOrder ?? position?.sortOrder ?? null,
    completedAtISO,
    completionDrivesDate,
  };
}

/** 정적 계획 + 동적 상태 → 날짜 앵커링된 task 인스턴스 목록 */
export function buildScheduledTasks(
  plan: StudyPlan,
  dyn: PlanDynamicState,
  basePlacement: BasePlacement = "page",
): ScheduledTask[] {
  const context = scheduledTaskBuildContext(plan, dyn, basePlacement);
  const out: ScheduledTask[] = [];
  for (const day of context.plan.days) {
    day.tasks.forEach((task, indexInDay) => {
      out.push(buildScheduledTask(context, day, task, indexInDay));
    });
  }
  return out;
}

/** 전체 동적 상태를 조립하지 않고 지정한 저장 키의 task만 파생한다. */
export function buildScheduledTaskByKey(
  plan: StudyPlan,
  dyn: PlanDynamicState,
  taskKey: string,
): ScheduledTask | null {
  const context = scheduledTaskBuildContext(plan, dyn);
  for (const day of context.plan.days) {
    const indexInDay = day.tasks.findIndex(
      (task) => taskKeyOf(day.dayNumber, task.path, task.codeLang) === taskKey,
    );
    if (indexInDay >= 0)
      return buildScheduledTask(
        context,
        day,
        day.tasks[indexInDay],
        indexInDay,
      );
  }
  return null;
}

/** 지정한 저장 키들에 속한 task만 파생한다. 같은 키를 공유하는 anchor 링크는 모두 유지한다. */
export function buildScheduledTasksByKeys(
  plan: StudyPlan,
  dyn: PlanDynamicState,
  taskKeys: Iterable<string>,
): ScheduledTask[] {
  const keys = new Set(taskKeys);
  if (!keys.size) return [];
  const context = scheduledTaskBuildContext(plan, dyn);
  const out: ScheduledTask[] = [];
  for (const day of context.plan.days) {
    day.tasks.forEach((task, indexInDay) => {
      if (!keys.has(taskKeyOf(day.dayNumber, task.path, task.codeLang))) return;
      out.push(buildScheduledTask(context, day, task, indexInDay));
    });
  }
  return out;
}

/**
 * 같은 날(칼럼) 안 정렬값. 사용자가 재정렬한 task 는 override.sortOrder 를, 그 외엔 기본 순서를 쓴다.
 * 기본 순서 = 계획 순서(이론<코딩) → originDayNumber → indexInDay 를 단일 수로 인코딩해 안정 정렬.
 * (originDayNumber·indexInDay 의 현실 범위에서 자리수가 겹치지 않게 1e6/1e3 간격.)
 */
function defaultOrderOf(task: ScheduledTask): number {
  const planIndex = Math.max(0, STUDY_PLAN_IDS.indexOf(task.planId));
  return planIndex * 1_000_000 + task.originDayNumber * 1_000 + task.indexInDay;
}

export function taskSortValue(task: ScheduledTask): number {
  return task.sortOrder ?? defaultOrderOf(task);
}

function bySortValue(a: ScheduledTask, b: ScheduledTask): number {
  return taskSortValue(a) - taskSortValue(b);
}

/** 특정 날짜에 표시될(이동 반영) task 들 */
export function tasksOnDate(
  scheduled: ScheduledTask[],
  dateISO: string,
): ScheduledTask[] {
  return scheduled
    .filter((t) => t.effectiveDateISO === dateISO)
    .sort(bySortValue);
}

/** 날짜별 그룹(캘린더용). 정렬된 [dateISO, tasks] 배열 */
export function groupByDate(scheduled: ScheduledTask[]): DayGroup[] {
  const map = new Map<string, ScheduledTask[]>();
  for (const t of scheduled) {
    const arr = map.get(t.effectiveDateISO);
    if (arr) arr.push(t);
    else map.set(t.effectiveDateISO, [t]);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([dateISO, tasks]) => {
      tasks.sort(bySortValue); // 같은 날 안 사용자 재정렬 반영
      const trackable = tasks.filter((t) => !t.skipped);
      return {
        dateISO,
        tasks,
        doneCount: trackable.filter((t) => t.done).length,
        totalCount: trackable.length,
      };
    });
}

/**
 * "오늘 기준 다시 분배" — 남은 미완료 페이지를 오늘~시험일에 선택한 모델로 재배치한다. (JCG-2310)
 *
 * 처음 분배(균등 베이스)를 "지금부터, 고른 모델로" 다시 적용하는 것:
 * - 대상은 미완료(완료·건너뛰기 제외) 페이지뿐 → 이미 출석/완료한 페이지는 정원을 안 잡고 그대로 둔다.
 * - distinct 페이지(taskKey)를 커리큘럼 순서로 pageDayOffsets(model) 가중치 분배에 따라 오늘~시험일에
 *   배치한다(각 날이 모델 비중만큼의 온전한 몫을 받음 — 첫날=오늘이 반 토막 나지 않는다).
 *
 * 같은 페이지(taskKey, 앵커만 다른 중복)를 가리키는 task 들은 같은 새 날짜로 간다. 반환: 옮겨야 할
 * (task, newDateISO, newSortOrder) 목록(날짜와 순서가 이미 제자리면 제외). taskKey 가 계획별로 겹치므로 **단일 계획분
 * scheduled(같은 planId·examISO)** 를 받는다 — 다계획은 호출부가 계획별로 나눠 호출한다.
 */
export function computeReplan(
  scheduled: ScheduledTask[],
  todayISO: string,
  examISO: string,
  model: ReplanModel = "even",
): { task: ScheduledTask; newDateISO: string; newSortOrder: number }[] {
  const pending = scheduled.filter((t) => !t.done && !t.skipped);
  if (pending.length === 0) return [];

  // 미완료 distinct 페이지(taskKey)를 커리큘럼 순서(Day → indexInDay 첫 등장)로 정렬.
  const firstPos = new Map<string, number>();
  for (const t of pending) {
    const pos = defaultOrderOf(t);
    const cur = firstPos.get(t.taskKey);
    if (cur === undefined || pos < cur) firstPos.set(t.taskKey, pos);
  }
  const pageKeys = [...firstPos.entries()]
    .sort((a, b) => a[1] - b[1])
    .map((e) => e[0]);
  // 시험날엔 일정을 넣지 않는다 → 학습 가능 일수 = diff(오늘→시험), 오늘~시험 전날(offset 0..diff-1).
  // 시험 당일(diff=0)·시험 후(diff<0)면 분배할 날이 없다. 예전엔 span=max(0,diff-1)=0 이라
  // pageDayOffsets 가 전부 0 → 남은 페이지를 모두 오늘(=시험일)에 몰았다(JCG-2484). 이제는 그 경우
  // 빈 배열을 반환해 오늘에 몰지 않고, 호출부가 "분배 불가" 안내를 띄운다. diff=1(시험 전날)이면
  // span=0 → 남은 하루(오늘)에 모으는 기존 동작 유지(유일한 학습일이라 정상).
  const daysUntilExam = diffDaysISO(todayISO, examISO);
  if (daysUntilExam <= 0) return [];
  const span = daysUntilExam - 1;
  const offsets = pageDayOffsets(pageKeys.length, span, model);

  const dateByKey = new Map<string, string>();
  pageKeys.forEach((k, j) =>
    dateByKey.set(k, addDaysISO(todayISO, offsets[j] ?? 0)),
  );

  const out: {
    task: ScheduledTask;
    newDateISO: string;
    newSortOrder: number;
  }[] = [];
  for (const t of pending) {
    const newDateISO = dateByKey.get(t.taskKey);
    const newSortOrder = firstPos.get(t.taskKey)!;
    // null로 지우면 사본의 최초 삽입 순서를 다시 상속한다. 명시적 재분배는
    // 페이지 묶음의 커리큘럼 순서를 저장하며, 날짜가 같아도 잘못된 순서는 복귀시킨다.
    if (
      newDateISO &&
      (t.effectiveDateISO !== newDateISO ||
        (t.sortOrder !== null && t.sortOrder !== newSortOrder))
    ) {
      out.push({ task: t, newDateISO, newSortOrder });
    }
  }
  return out;
}

/** 진행/온트랙 지표 계산 */
export function computeMetrics(
  plan: StudyPlan,
  scheduled: ScheduledTask[],
  dyn: PlanDynamicState,
  todayISO: string,
): ScheduleMetrics {
  plan = personalStudyPlan(plan, dyn);
  // 시험일은 회차 고정값(plan.examDateISO, 예 2026-07-19). 개인 시작일/계획 길이로 재앵커링하지
  // 않는다 — 그러면 같은 시험인데도 이론(28일)·코딩(42일)의 D-day 가 달라지는 버그가 난다.
  const examDateISO = plan.examDateISO;
  // 페이지 균등 분배라 "오늘이 며칠차"는 오늘까지 예정된 distinct 페이지 "개수"를 커리큘럼 순서로
  // 환산한 위치의 Day 로 본다. max originDayNumber 를 쓰면 고-Day 페이지 한 장을 이른 날짜로 옮기는
  // 것만으로 "N일차"가 과대 표시되므로(JCG-2485), 페이지 개수를 커리큘럼 인덱스로 환산한다. 이동이
  // 없으면 효과 날짜가 커리큘럼 순서대로 증가하니 옛 "max originDayNumber" 와 동일 결과다. 시작 전이면 null.
  const { order: pageOrder } = planPageOrder(plan);
  const dayByKey = new Map<string, number>();
  const dueKeys = new Set<string>();
  for (const t of scheduled) {
    if (!dayByKey.has(t.taskKey)) dayByKey.set(t.taskKey, t.originDayNumber);
    if (t.scheduledDateISO <= todayISO) dueKeys.add(t.taskKey);
  }
  const reachedKey =
    dueKeys.size > 0
      ? pageOrder[Math.min(dueKeys.size, pageOrder.length) - 1]
      : undefined;
  const todayDayNumber = reachedKey ? (dayByKey.get(reachedKey) ?? null) : null;

  const trackable = scheduled.filter((t) => !t.skipped);
  const trackableTasks = trackable.length;
  const doneTotal = trackable.filter((t) => t.done).length;

  // 예정 기준이므로 scheduledDateISO 를 쓴다 — 완료로 오늘에 옮겨진 미래 항목을 여기서 세면
  // scheduledByToday 와 doneTotal 이 함께 올라가 "앞서 공부(ahead)" 판정이 상쇄된다(JCG-3547).
  const byToday = trackable.filter((t) => t.scheduledDateISO <= todayISO);
  const scheduledByToday = byToday.length;
  const doneByToday = byToday.filter((t) => t.done).length;
  // 밀림/앞섬은 "오늘까지 예정(scheduledByToday)" 대비 "전체 완료(doneTotal, 오늘·내일 상관없이)"로
  // 판정한다 — 미래 task 를 미리 해두면(앞서 공부) 그 완료도 진척으로 인정한다.
  const backlog = Math.max(0, scheduledByToday - doneTotal);

  const daysUntilExam = Math.max(0, diffDaysISO(todayISO, examDateISO));
  const remainingTasks = Math.max(0, trackableTasks - doneTotal);
  const requiredPacePerDay =
    daysUntilExam > 0 ? remainingTasks / daysUntilExam : remainingTasks;

  const avgPerDay = plan.totalDays > 0 ? trackableTasks / plan.totalDays : 0;
  const behindDays = avgPerDay > 0 ? backlog / avgPerDay : backlog;

  let status: OnTrackStatus;
  if (!dyn.subscribed) {
    status = "not-started";
  } else if (trackableTasks > 0 && doneTotal >= trackableTasks) {
    status = "done";
  } else if (backlog <= 0) {
    status = doneTotal > scheduledByToday ? "ahead" : "on-track";
  } else if (behindDays <= 1.2) {
    status = "slightly-behind";
  } else {
    status = "behind";
  }

  return {
    planId: plan.planId,
    subscribed: dyn.subscribed,
    startDateISO: dyn.startDateISO,
    examDateISO,
    totalDays: plan.totalDays,
    todayDateISO: todayISO,
    todayDayNumber,
    trackableTasks,
    doneTotal,
    scheduledByToday,
    doneByToday,
    backlog,
    daysUntilExam,
    remainingTasks,
    requiredPacePerDay,
    status,
  };
}

// ── 낙관적 토글 reducer (클라 공용) ────────────────────────────────────────────

/** 실패한 항목만 요청 전 기록으로 복원한다. 다른 항목의 병행 저장은 유지한다. */
export function restoreTaskDynamicState(
  prev: PlanDynamicStateMap,
  task: ScheduledTask,
  before: PlanDynamicState,
  restoreAttendance = false,
): PlanDynamicStateMap {
  if (!prev) return prev;
  const cur = prev[task.planId];
  const overrides = { ...cur.overrides };
  const original = before.overrides[task.taskKey];
  if (original) overrides[task.taskKey] = original;
  else delete overrides[task.taskKey];

  let attendedAt = cur.attendedAt;
  if (restoreAttendance && task.attendanceBacked && task.slug) {
    attendedAt = { ...attendedAt };
    const key = attendanceKeyOf(task.slug, task.codeLang);
    if (before.attendedAt[key]) attendedAt[key] = before.attendedAt[key];
    else delete attendedAt[key];
    if (task.codeLang) {
      // 순수 slug는 언어별 출석 중 가장 이른 시각이다. 다른 언어의 병행 완료를 보존한다.
      const times = Object.entries(attendedAt)
        .filter(([k]) => k.startsWith(`${task.slug}::`))
        .map(([, at]) => at)
        .sort();
      if (times.length) attendedAt[task.slug] = times[0];
      else delete attendedAt[task.slug];
    }
  }
  return { ...prev, [task.planId]: { ...cur, overrides, attendedAt } };
}

/**
 * 완료 토글의 낙관적 override 갱신(순수). (JCG-2486)
 *
 * /my/schedule(useScheduleData) 와 대시보드 "오늘의 학습"(DashboardTodaySchedule) 양쪽이
 * 동일하게 사용한다. 출석 연동 task 는 attendedAt 에 시각을 넣고 빼고, 그 외에는
 * override.status 와 completedAt 을 토글한다. 자동 이동 설정이 켜져 있으면 완료하는 순간
 * 그날(KST)을 completionDate 에 적어 서버(toggleTaskCompletion)와 같은 자리에 그린다(JCG-3653);
 * 완료를 취소하면 지워 예정 날짜로 돌아간다.
 *
 * completedAtISO 는 이 모듈이 시계를 읽지 않도록(파일 상단 규칙) 호출부가 넘긴다.
 */
export function applyToggleOverride(
  prev: PlanDynamicStateMap,
  task: ScheduledTask,
  done: boolean,
  completedAtISO: string,
): PlanDynamicStateMap {
  if (!prev) return prev;
  const cur = prev[task.planId];
  if (!cur) return prev;

  const overrides = { ...cur.overrides };
  const existing = overrides[task.taskKey];
  const next: TaskOverride = done
    ? {
        ...existing, // movedToDate·sortOrder 등 기존 필드 보존
        status: "done",
        movedToDate: existing?.movedToDate ?? null,
        // 출석 연동 task 의 완료 시각 원천은 출석이다. 여기에 적으면 그 시각을 덮어쓴다.
        ...(task.attendanceBacked ? {} : { completedAt: completedAtISO }),
        completionDate:
          cur.completionMoveEnabled === true
            ? toKstDateStr(completedAtISO)
            : (existing?.completionDate ?? null),
      }
    : {
        ...existing,
        status: "none",
        movedToDate: existing?.movedToDate ?? null,
        completedAt: null,
        completionDate: null,
      };
  if (isDefaultOverride(next)) delete overrides[task.taskKey];
  else overrides[task.taskKey] = next;

  let attendedAt = cur.attendedAt;
  if (task.attendanceBacked && task.slug) {
    const next = { ...cur.attendedAt };
    const composite = attendanceKeyOf(task.slug, task.codeLang);
    if (done) {
      next[composite] = completedAtISO;
      // 언어 무관 매칭용 순수 slug 키는 그 페이지의 가장 이른 출석 시각을 유지한다.
      const plain = next[task.slug];
      if (!plain || completedAtISO < plain) next[task.slug] = completedAtISO;
    } else {
      delete next[composite];
      // 다른 언어 출석이 남아있으면 순수 slug 는 그중 가장 이른 시각으로, 없으면 제거.
      const others = Object.entries(next)
        .filter(([k]) => k.startsWith(`${task.slug}::`))
        .map(([, v]) => v)
        .sort();
      if (others.length > 0) next[task.slug] = others[0];
      else delete next[task.slug];
    }
    attendedAt = next;
  }

  return { ...prev, [task.planId]: { ...cur, overrides, attendedAt } };
}
